// src/components/Abwesenheiten.js

import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import {
  collection,
  addDoc,
  onSnapshot,
  deleteDoc,
  doc,
  query,
  where,
  updateDoc
} from 'firebase/firestore';
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import PropTypes from 'prop-types';
import axios from 'axios';

function Abwesenheiten({ tenantId, selectedEmployee, showSnackbar, appointments }) {
  const [absenceStartDate, setAbsenceStartDate] = useState('');
  const [absenceEndDate, setAbsenceEndDate] = useState('');
  const [absenceReason, setAbsenceReason] = useState('');
  const [employeeAbsences, setEmployeeAbsences] = useState([]);
  const [editingAbsence, setEditingAbsence] = useState(null);

  // Neuer State für kollidierende Termine
  const [conflictingAppointments, setConflictingAppointments] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Neuer State für dauerhaft sichtbare Konflikte
  const [visibleConflicts, setVisibleConflicts] = useState([]);

  // State für appointmentTypes
  const [appointmentTypes, setAppointmentTypes] = useState([]);

  // Abwesenheiten laden, wenn Mitarbeiter gewechselt wird
  useEffect(() => {
    if (!tenantId || !selectedEmployee) {
      setEmployeeAbsences([]);
      return;
    }
    const qAbsences = query(
      collection(db, 'tenants', tenantId, 'absences'),
      where('employeeId', '==', selectedEmployee)
    );
    const unsubAbsences = onSnapshot(qAbsences, (snapshot) => {
      const data = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      // Absences sortieren nach Datum
      data.sort((a, b) => (a.date > b.date ? 1 : -1));
      setEmployeeAbsences(data);
    });
    return () => unsubAbsences();
  }, [tenantId, selectedEmployee]);

  // appointmentTypes laden
  useEffect(() => {
    if (!tenantId) return;
    const unsubTypes = onSnapshot(collection(db, 'tenants', tenantId, 'appointmentTypes'), (snapshot) => {
      const types = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setAppointmentTypes(types);
    });
    return () => unsubTypes();
  }, [tenantId]);

  // Berechnung der sichtbaren Konflikte bei Änderung von Abwesenheiten oder Terminen
  useEffect(() => {
    if (!tenantId || !selectedEmployee) {
      setVisibleConflicts([]);
      return;
    }

    // Funktion zur Ermittlung kollidierender Termine
    const calculateVisibleConflicts = () => {
      const conflictsSet = new Set(); // Zum Verhindern von Duplikaten
      const conflicts = [];

      employeeAbsences.forEach(absence => {
        const absDate = new Date(absence.date);
        appointments.forEach(appt => {
          if (appt.employeeId !== selectedEmployee) return;
          
          // Überprüfen, ob der Termin nicht storniert ist
          if (!appt.hasOwnProperty('canceled') || appt.canceled !== false) return;
  
          const apptDate = new Date(appt.date);
          // Überprüfen, ob der Termin am selben Tag liegt wie die Abwesenheit
          if (apptDate.toDateString() === absDate.toDateString()) {
            if (!conflictsSet.has(appt.id)) {
              const type = appointmentTypes.find(t => t.id === appt.appointmentTypeId);
              conflicts.push({
                ...appt,
                appointmentTypeName: type ? type.name : 'Unbekannt'
              });
              conflictsSet.add(appt.id);
            }
          }
        });
      });
  
      console.log('Gefundene Konflikte:', conflicts); // Debugging-Statement
      setVisibleConflicts(conflicts);
    };
  
    calculateVisibleConflicts();
  }, [tenantId, selectedEmployee, employeeAbsences, appointments, appointmentTypes]);
  // Validierung Abwesenheit
  const validateAbsenceInputs = () => {
    if (!selectedEmployee || !absenceStartDate || !absenceEndDate || !absenceReason) {
      showSnackbar('Bitte alle Felder für Abwesenheit ausfüllen!');
      return false;
    }
    const start = new Date(absenceStartDate);
    const end = new Date(absenceEndDate);
    if (end < start) {
      showSnackbar('Enddatum muss nach dem Startdatum liegen!');
      return false;
    }
    return true;
  };

// Funktion zur Ermittlung kollidierender Termine beim Hinzufügen
const findConflictingAppointments = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  return appointments.filter(appointment => {
    // Überprüfen, ob der Termin zum ausgewählten Mitarbeiter gehört
    if (appointment.employeeId !== selectedEmployee) return false;
    
    // Überprüfen, ob der Termin nicht storniert ist
    if (!appointment.hasOwnProperty('canceled') || appointment.canceled !== false) return false;
    
    const appointmentDate = new Date(appointment.date);
    
    // Überprüfen, ob das Termin-Datum innerhalb des Abwesenheitszeitraums liegt
    return appointmentDate >= start && appointmentDate <= end;
  });
};



  // (D) Neue Abwesenheit hinzufügen (mehrere Tage)
  const handleAddAbsence = async () => {
    if (!validateAbsenceInputs()) return;

    const start = new Date(absenceStartDate);
    const end = new Date(absenceEndDate);

    const absenceDays = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      absenceDays.push(new Date(d));
    }

    try {
      const promises = absenceDays.map((day) =>
        addDoc(collection(db, 'tenants', tenantId, 'absences'), {
          employeeId: selectedEmployee,
          date: day.toISOString().split('T')[0],
          reason: absenceReason,
        })
      );
      await Promise.all(promises);
      showSnackbar('Abwesenheit hinzugefügt!');

      // Konfliktprüfung
      const conflicts = findConflictingAppointments(absenceStartDate, absenceEndDate);
      if (conflicts.length > 0) {
        // Map appointmentTypeId to typeName
        const conflictsWithType = conflicts.map(appt => {
          const type = appointmentTypes.find(t => t.id === appt.appointmentTypeId);
          return {
            ...appt,
            appointmentTypeName: type ? type.name : 'Unbekannt'
          };
        });
        setConflictingAppointments(conflictsWithType);
        setDialogOpen(true);
      }

      resetAbsenceForm();
    } catch (error) {
      console.error('Fehler beim Hinzufügen der Abwesenheit:', error);
      showSnackbar('Fehler beim Hinzufügen der Abwesenheit.');
    }
  };

  // (E) Abwesenheit updaten (nur ein Tag)
  const handleUpdateAbsence = async () => {
    if (!editingAbsence) return;
    if (!absenceStartDate || !absenceReason) {
      showSnackbar('Bitte alle Felder ausfüllen!');
      return;
    }
    try {
      const absenceRef = doc(db, 'tenants', tenantId, 'absences', editingAbsence.id);
      await updateDoc(absenceRef, {
        date: absenceStartDate,
        reason: absenceReason,
      });
      showSnackbar('Abwesenheit aktualisiert!');
      setEditingAbsence(null);
      resetAbsenceForm();
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Abwesenheit:', error);
      showSnackbar('Fehler beim Aktualisieren der Abwesenheit.');
    }
  };

  // (F) Abwesenheit löschen
  const handleDeleteAbsence = async (id) => {
    try {
      const absenceRef = doc(db, 'tenants', tenantId, 'absences', id);
      await deleteDoc(absenceRef);
      showSnackbar('Abwesenheit gelöscht!');
      if (editingAbsence && editingAbsence.id === id) {
        setEditingAbsence(null);
        resetAbsenceForm();
      }
    } catch (error) {
      console.error('Fehler beim Löschen der Abwesenheit:', error);
      showSnackbar('Fehler beim Löschen der Abwesenheit.');
    }
  };

  // Reset-Funktionen
  const resetAbsenceForm = () => {
    setAbsenceStartDate('');
    setAbsenceEndDate('');
    setAbsenceReason('');
  };

  // Formulardaten in State laden (beim Bearbeiten)
  const startEditingAbsence = (absence) => {
    setEditingAbsence(absence);
    // Für eine einzelne Abwesenheit: Start- und Enddatum gleich
    setAbsenceStartDate(absence.date);
    setAbsenceEndDate(absence.date);
    setAbsenceReason(absence.reason);
  };

  const cancelEditingAbsence = () => {
    setEditingAbsence(null);
    resetAbsenceForm();
  };

  // Funktion zum Schließen des Dialogs
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // Funktion zum Entfernen der angezeigten Konflikte
  const handleClearConflicts = () => {
    setVisibleConflicts([]);
  };

  // Funktion zum Absagen aller Konflikte
  const handleCancelAllConflicts = async () => {
    if (visibleConflicts.length === 0) {
      showSnackbar('Keine Konflikte zum Absagen.');
      return;
    }

    // Bestätigungsdialog
    const confirm = window.confirm('Möchten Sie alle kollidierenden Termine absagen?');
    if (!confirm) {
      return;
    }

    // Eingabe des Absagegrundes
    const reason = prompt('Bitte geben Sie den Grund für die Absage ein:', 'Abwesenheit des Mitarbeiters');
    if (!reason) {
      showSnackbar('Absage abgebrochen.');
      return;
    }

    // Sammle die Appointment IDs
    const appointmentIds = visibleConflicts.map(appt => appt.id);

    try {
      const functionUrl = process.env.REACT_APP_CANCEL_APPOINTMENTS_URL; // Nutze die Umgebungsvariable

      const response = await axios.post(functionUrl, {
        tenantId,
        appointmentIds,
        cancellationReason: reason,
      }, {
        headers: {
          'Content-Type': 'application/json',
          // Optional: Füge Auth-Token hinzu, falls die Funktion Auth erfordert
          // 'Authorization': `Bearer ${userToken}`,
        }
      });

      if (response.status === 200) {
        showSnackbar('Alle kollidierenden Termine wurden erfolgreich abgesagt.');
      } else {
        showSnackbar('Fehler beim Absagen der Termine.');
      }
    } catch (error) {
      console.error('Fehler beim Absagen der Termine:', error);
      showSnackbar('Fehler beim Absagen der Termine.');
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" gutterBottom>
        Abwesenheiten verwalten
      </Typography>

      {/* Liste vorhandener Abwesenheiten */}
      {employeeAbsences.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Vorhandene Abwesenheiten
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Grund</TableCell>
                <TableCell>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employeeAbsences.map((abs) => (
                <TableRow key={abs.id}>
                  <TableCell>{abs.date}</TableCell>
                  <TableCell>{abs.reason}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="primary"
                      sx={{ mr: 1 }}
                      onClick={() => startEditingAbsence(abs)}
                    >
                      Bearbeiten
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleDeleteAbsence(abs.id)}
                    >
                      Löschen
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Formular für neue/bearbeitete Abwesenheit */}
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Startdatum der Abwesenheit"
              type="date"
              variant="outlined"
              fullWidth
              value={absenceStartDate}
              onChange={(e) => setAbsenceStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={editingAbsence ? false : false}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Enddatum der Abwesenheit"
              type="date"
              variant="outlined"
              fullWidth
              value={absenceEndDate}
              onChange={(e) => setAbsenceEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={editingAbsence ? true : false}
              // Beim Editieren: Start- und Enddatum meist gleich
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Grund der Abwesenheit"
              type="text"
              variant="outlined"
              fullWidth
              value={absenceReason}
              onChange={(e) => setAbsenceReason(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            {editingAbsence ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mr: 2 }}
                  onClick={handleUpdateAbsence}
                >
                  Abwesenheit aktualisieren
                </Button>
                <Button variant="outlined" onClick={cancelEditingAbsence}>
                  Abbrechen
                </Button>
              </>
            ) : (
              <Button variant="contained" color="error" onClick={handleAddAbsence}>
                Abwesenheit hinzufügen
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Dialog für kollidierende Termine */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Kollidierende Termine gefunden</DialogTitle>
        <DialogContent>
          <Typography>
            Beim Hinzufügen dieser Abwesenheit wurden folgende Termine gefunden, die mit der Abwesenheit kollidieren:
          </Typography>
          <List>
            {conflictingAppointments.map((appt) => (
              <ListItem key={appt.id} alignItems="flex-start">
                <ListItemText
                  primary={`Termin am ${appt.date} um ${appt.startTime}`}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="textPrimary">
                        Kunde: {appt.customerName}
                      </Typography>
                      <br />
                      Email: {appt.customerEmail}<br />
                      Telefon: {appt.customerPhoneNumber}<br />
                      Termin-Typ: {appt.appointmentTypeName}<br />
                      Dauer: {appt.duration} Minuten
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
          {conflictingAppointments.length === 0 && (
            <Typography>Keine kollidierenden Termine gefunden.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Abschnitt für dauerhaft sichtbare kollidierende Termine */}
      {visibleConflicts.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Divider />
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Kollidierende Termine
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Startzeit</TableCell>
                <TableCell>Kunde</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Telefon</TableCell>
                <TableCell>Termin-Typ</TableCell>
                <TableCell>Dauer (Minuten)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleConflicts.map((appt) => (
                <TableRow key={appt.id}>
                  <TableCell>{appt.date}</TableCell>
                  <TableCell>{appt.startTime}</TableCell>
                  <TableCell>{appt.customerName}</TableCell>
                  <TableCell>{appt.customerEmail}</TableCell>
                  <TableCell>{appt.customerPhoneNumber}</TableCell>
                  <TableCell>{appt.appointmentTypeName}</TableCell>
                  <TableCell>{appt.duration}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancelAllConflicts}
              sx={{ mr: 2 }}
            >
              Alle Konflikte absagen
            </Button>
            {/* <Button variant="outlined" color="secondary" onClick={handleClearConflicts}>
              Alle Konflikte entfernen
            </Button> */}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

Abwesenheiten.propTypes = {
  tenantId: PropTypes.string.isRequired,
  selectedEmployee: PropTypes.string.isRequired,
  showSnackbar: PropTypes.func.isRequired,
  appointments: PropTypes.array.isRequired, // appointments Prop
};

export default Abwesenheiten;